import React, { useCallback, useContext, useEffect } from 'react';
import { bool, func, shape, string } from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import { onDoRequestResendEmail } from 'Redux/auth';
import { onDoRequestMembership } from 'Redux/profile';
import { AuthContext } from '@context/auth';
import { Button, MessageBox } from '@ajaibid/components';
import { noop } from '@ajaibid/utils';
import IconWarning from '@assets/Icon/svg/Warning';

import { useUserData } from '@services/users/me';
import Account from './Account';
import Prime from './Prime';
import PaymentInfo from './PaymentInfo';
import SlideBanner from './SlideBanner';
import BuyingPower from './BuyingPower';
import MenuList from './MenuList';

import { styHeaderBg, styTextMsgBox, styTitleMsgBox } from './style';

const ProfileMenu = ({
  fetchPrimeTrial,
  imgProfile,
  isPremium,
  isPremiumTrial,
  primeTrialData,
  stockKycVerified,
  stockKycError,
}) => {
  const dispatch = useDispatch();
  const { userData } = useUserData();
  const { homeData } = useSelector((state) => state.auth);
  const { membershipData, profileData: profileRdn } = useSelector((state) => state.profile);
  const { emailVerified, userNotKycYet, stockKycVerifying, mfKycVerifying } =
    useContext(AuthContext);

  useEffect(() => {
    if (!membershipData && isPremium) {
      dispatch(onDoRequestMembership());
    }
    if (isPremiumTrial && !primeTrialData) {
      fetchPrimeTrial();
    }
  }, [isPremium, isPremiumTrial]);

  const resendEmailVerification = useCallback(() => {
    dispatch(onDoRequestResendEmail({ email: homeData?.email }));
  });

  const titleMsgBox = <span className={styTitleMsgBox}>Kamu belum verifikasi email.</span>;

  const textMsgBox = (
    <>
      <span className={styTextMsgBox}>
        Klik tautan yang telah dikirimkan ke email terdaftar untuk lanjut verifikasi akun Ajaib.
      </span>
      <Button
        variant="naked"
        size="xs"
        onClick={resendEmailVerification}
        style={{ background: 'none', border: 'none', padding: 0, marginTop: 8 }}
      >
        Kirim Ulang Email
      </Button>
    </>
  );

  return (
    <div style={{ overflowY: 'scroll' }}>
      <div className={styHeaderBg({ isPremium, isPremiumTrial })} />
      <Account
        imgProfile={imgProfile}
        isPremium={isPremium}
        isPremiumTrial={isPremiumTrial}
        userData={userData}
      />
      {!emailVerified ? (
        <MessageBox
          title={titleMsgBox}
          text={textMsgBox}
          type="caution"
          icon={<IconWarning />}
          style={{ margin: '50px 16px 0', padding: '16px 12px' }}
        />
      ) : (
        <>
          {(isPremium || isPremiumTrial) && (
            <Prime
              membershipData={membershipData}
              isPremiumTrial={isPremiumTrial}
              primeTrialData={primeTrialData}
            />
          )}
          {!userNotKycYet && stockKycVerified && !stockKycError && (
            <PaymentInfo
              profileRdn={profileRdn?.result}
              isPremium={isPremium}
              isPremiumTrial={isPremiumTrial}
            />
          )}
        </>
      )}

      <SlideBanner
        emailVerified={emailVerified}
        isPremium={isPremium}
        isPremiumTrial={isPremiumTrial}
        userNotKycYet={userNotKycYet}
        stockKycVerifying={stockKycVerifying}
        mfKycVerifying={mfKycVerifying}
        stockKycVerified={stockKycVerified}
        stockKycError={stockKycError}
      />
      <BuyingPower />
      <MenuList />
    </div>
  );
};

ProfileMenu.defaultProps = {
  fetchPrimeTrial: noop,
  imgProfile: '',
  isPremium: false,
  isPremiumTrial: false,
  primeTrialData: {},
  stockKycVerified: false,
  stockKycError: false,
};

ProfileMenu.propTypes = {
  fetchPrimeTrial: func,
  imgProfile: string,
  isPremium: bool,
  isPremiumTrial: bool,
  primeTrialData: shape({}),
  stockKycVerified: bool,
  stockKycError: bool,
};

export default ProfileMenu;
